import { useState, useRef } from 'react';

const inputClass = [
  'w-full px-4 py-4 text-[15px] outline-none transition-colors duration-200',
  'bg-[#F9F9F7] border border-[#1A1A1A]/12',
  'text-[#061e45] placeholder:text-[#1A1A1A]/30',
  'focus:border-[#061e45]',
].join(' ');

const labelClass = 'block text-[#061e45] text-[15px] font-medium mb-2';

export default function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    processingVolume: '',
    currentProcessor: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;
    setSubmitting(true);
    setError('');

    const message = [
      formData.processingVolume ? `Monthly Processing Volume: ${formData.processingVolume}` : '',
      formData.currentProcessor ? `Current Processor: ${formData.currentProcessor}` : '',
    ].filter(Boolean).join('\n');

    const body = new FormData();
    body.append('name', formData.name);
    body.append('email', formData.email);
    body.append('company', formData.company);
    body.append('phone', formData.phone);
    body.append('message', message || '');
    if (file) body.append('file', file);

    try {
      const res = await fetch('/api/contact', { method: 'POST', body });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setSubmitted(true);
        setFormData({ name: '', company: '', email: '', phone: '', processingVolume: '', currentProcessor: '' });
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-[#F9F9F7] border border-[#1A1A1A]/8 p-10 md:p-16">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 flex items-center justify-center bg-[#d3b366]/15">
            <i className="ri-check-line text-[#a8894a] text-2xl" />
          </div>
        </div>
        <h3
          className="text-[#061e45] text-2xl font-light mb-3"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Request Submitted.
        </h3>
        <p
          className="text-[#1A1A1A]/55 text-[15px] leading-relaxed"
          style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
        >
          Thanks for reaching out. We'll review your inquiry and get back to you within one business day.
        </p>
      </div>
    );
  }

  return (
    <div>
      <p
        className="text-[#a8894a] text-[10px] uppercase tracking-[0.25em] font-medium mb-5"
        style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
      >
        Get Started
      </p>
      <h2
        className="text-[#061e45] text-3xl md:text-4xl font-light leading-snug mb-10"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        Tell Us About Your Business.
      </h2>

      <form onSubmit={handleSubmit} className="flex flex-col gap-8" style={{ fontFamily: "'Google Sans Flex', sans-serif" }}>

        {/* Row 1: Full Name + Business Name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className={labelClass}>Full Name</label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className={inputClass} />
          </div>
          <div>
            <label htmlFor="company" className={labelClass}>Business Name</label>
            <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className={inputClass} />
          </div>
        </div>

        {/* Row 2: Email + Phone */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label htmlFor="email" className={labelClass}>Email Address</label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} />
          </div>
          <div>
            <label htmlFor="phone" className={labelClass}>Phone Number</label>
            <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} />
          </div>
        </div>

        {/* Row 3: Monthly Processing Volume */}
        <div>
          <label htmlFor="processingVolume" className={labelClass}>Monthly Processing Volume</label>
          <div className="relative">
            <select
              id="processingVolume"
              name="processingVolume"
              value={formData.processingVolume}
              onChange={handleChange}
              className={[inputClass, 'appearance-none pr-10 cursor-pointer'].join(' ')}
              style={{ color: formData.processingVolume ? '#061e45' : 'rgba(26,26,26,0.3)' }}
            >
              <option value="" disabled>Select volume range</option>
              <option value="Under $50K/mo">Under $50K/mo</option>
              <option value="$50K–$250K/mo">$50K–$250K/mo</option>
              <option value="$250K–$500K/mo">$250K–$500K/mo</option>
              <option value="$500K–$1M/mo">$500K–$1M/mo</option>
              <option value="$1M+/mo">$1M+/mo</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#061e45]/40 text-lg" />
          </div>
        </div>

        {/* Row 4: Current Processor */}
        <div>
          <label htmlFor="currentProcessor" className={labelClass}>
            Current Processor <span className="text-[#1A1A1A]/35 font-normal">(Optional)</span>
          </label>
          <input
            type="text"
            id="currentProcessor"
            name="currentProcessor"
            value={formData.currentProcessor}
            onChange={handleChange}
            placeholder="e.g., Square, Stripe, Toast"
            className={inputClass}
          />
        </div>

        {/* Row 5: Upload Statement */}
        <div>
          <label className={labelClass}>
            Upload Processing Statement <span className="text-[#1A1A1A]/35 font-normal">(Optional)</span>
          </label>
          <label
            htmlFor="file"
            className="flex flex-col items-center justify-center w-full cursor-pointer border border-dashed border-[#1A1A1A]/20 bg-[#F9F9F7] px-6 py-8 transition-colors duration-200 hover:border-[#061e45]/40 hover:bg-[#f0f0ed]"
          >
            {file ? (
              <div className="flex items-center gap-3 text-[#061e45]">
                <i className="ri-file-text-line text-2xl text-[#d3b366]" />
                <div>
                  <p className="text-[14px] font-medium">{file.name}</p>
                  <p className="text-[12px] text-[#1A1A1A]/40">{(file.size / 1024).toFixed(0)} KB</p>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="ml-4 text-[#1A1A1A]/30 hover:text-red-400 transition-colors"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-center">
                <i className="ri-upload-cloud-line text-3xl text-[#1A1A1A]/25" />
                <p className="text-[14px] text-[#1A1A1A]/50">
                  Drop your statement here or <span className="text-[#061e45] font-medium underline underline-offset-2">browse</span>
                </p>
                <p className="text-[12px] text-[#1A1A1A]/35">PDF, Excel, CSV, or image — up to 10 MB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              id="file"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.xls,.xlsx,.csv"
              onChange={handleFileChange}
              className="hidden"
            />
          </label>
        </div>

        {error && (
          <p className="text-red-500 text-sm">{error}</p>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-5 bg-[#061e45] text-white text-[14px] font-semibold tracking-wide hover:bg-[#0a2d5c] transition-colors duration-200 cursor-pointer disabled:opacity-60"
        >
          {submitting ? 'Submitting...' : 'Submit Request'}
        </button>

      </form>
    </div>
  );
}
